import React, { useState, useEffect } from 'react'
import { Mail, Calendar, TrendingUp, User, Send, ArrowLeft, BarChart3 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { supabase } from '@/integrations/supabase/client'
import { resolveClientFilter } from '@/lib/resolve-client-filter'
import { utcStartOfDay, utcEndOfDay, utcStartOfMonth, utcStartOfPrevMonth, utcEndOfPrevMonth } from '@/lib/utc-date-utils'
import { TimeframeSelector } from './TimeframeSelector'
import { EmailEventDetailModal } from '@/components/shared/EmailEventDetailModal'
import { EnhancedDataTable } from './EnhancedDataTable'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'

interface EmailsSentDetailViewProps {
  onBack: () => void
  clientFilter?: string
  timeFrame?: string
  customDateRange?: {
    from: Date
    to: Date
  }
  onTimeFrameChange?: (timeFrame: string) => void
  onCustomDateRangeChange?: (dateRange: { from: Date; to: Date } | undefined) => void
}

interface EmailMetrics {
  totalEvents: number
  dailyAverage: number
  activeCampaigns: number
}

interface EmailTypeData {
  type: string
  count: number
  percentage: number
}

interface DailyEmailData {
  date: string
  fullDate: string
  emails: number
  realPerson: number
  company: number
  sequence1: number
  sequence2: number
  sequence3: number
  sequenceOther: number
}

interface CampaignData {
  campaign: string
  count: number
  percentage: number
}

interface DailyCampaignData {
  date: string
  fullDate: string
  [key: string]: string | number // Dynamic campaign names
}

export const EmailsSentDetailView = ({ 
  onBack, 
  clientFilter = 'all', 
  timeFrame = 'MTD', 
  customDateRange,
  onTimeFrameChange,
  onCustomDateRangeChange
}: EmailsSentDetailViewProps) => {
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [detailRecords, setDetailRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState<EmailMetrics>({
    totalEvents: 0,
    dailyAverage: 0,
    activeCampaigns: 0
  })
  const [emailTypeData, setEmailTypeData] = useState<EmailTypeData[]>([])
  const [dailyData, setDailyData] = useState<DailyEmailData[]>([])
  const [campaignData, setCampaignData] = useState<CampaignData[]>([])
  const [dailyCampaignData, setDailyCampaignData] = useState<DailyCampaignData[]>([])
  const [chartLoading, setChartLoading] = useState(true)

  // Helper function to get date range based on timeframe
  // All boundaries use UTC to match Postgres timestamps regardless of browser timezone.
  const getDateRange = () => {
    if (customDateRange && customDateRange.from && customDateRange.to) {
      return {
        startDate: utcStartOfDay(customDateRange.from).toISOString(),
        endDate: utcEndOfDay(customDateRange.to).toISOString()
      }
    }

    const now = new Date()
    let startDate: Date
    let endDate: Date = now

    switch (timeFrame) {
      case 'Today':
        startDate = utcStartOfDay(now)
        break
      case 'Yesterday': {
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        startDate = utcStartOfDay(yesterday)
        endDate = utcStartOfDay(now)
        return {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      }
      case 'Last 7 Days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'Last 30 Days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'Last Month':
      case 'LAST_MONTH':
        startDate = utcStartOfPrevMonth(now)
        endDate = utcEndOfPrevMonth(now)
        return {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      case 'MTD':
        startDate = utcStartOfMonth(now)
        endDate = now
        break
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  }

  // Fetch metrics based on timeframe and client filter
  const fetchMetrics = async () => {
    const { startDate, endDate } = getDateRange()
    const numericClientId = await resolveClientFilter(clientFilter)

    console.log('🔍 DEBUG EmailsSentDetailView - Date Range:', {
      timeFrame,
      startDate,
      endDate,
      startDateObj: new Date(startDate),
      endDateObj: new Date(endDate)
    })

    let query = supabase
      .from('obsync_dynamic_reports')
      .select('event_type, campaign_name, created_at, time_sent', { count: 'exact' })
      .eq('event_type', 'EMAIL_SENT')
      .not('time_sent', 'is', null)
      .gte('time_sent', startDate)
      .lt('time_sent', endDate)

    // Apply client filter
    if (numericClientId) {
      query = query.eq('client_id', numericClientId)
    }

    const { data, count, error } = await query

    console.log('📊 Email metrics query DEBUG:', {
      timeFrame,
      startDate,
      endDate,
      clientFilter,
      count,
      error,
      sampleData: data?.slice(0, 3)?.map(record => ({
        time_sent: record.time_sent,
        created_at: record.created_at,
        campaign_name: record.campaign_name
      }))
    })

    if (error) {
      console.error('Error fetching metrics:', error)
      return
    }

    const totalEvents = count || 0
    const daysDiff = Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)))
    const dailyAverage = Math.round(totalEvents / daysDiff)
    
    // Count unique campaigns
    const uniqueCampaigns = new Set(data?.map(record => record.campaign_name).filter(Boolean)).size

    setMetrics({
      totalEvents,
      dailyAverage,
      activeCampaigns: uniqueCampaigns
    })
  }

  // Fetch detailed records — limited to most recent 200 for table display
  const fetchDetailRecords = async () => {
    try {
      setLoading(true)
      const { startDate, endDate } = getDateRange()
      const numericClientId = await resolveClientFilter(clientFilter)

      let query = supabase
        .from('obsync_dynamic_reports')
        .select(`
          id, event_type, email_type, from_email, to_email, to_name,
          campaign_name, campaign_id, client_id, subject, time_sent, created_at,
          sequence_number, campaign_status, lead_email
        `)
        .eq('event_type', 'EMAIL_SENT')
        .not('time_sent', 'is', null)
        .gte('time_sent', startDate)
        .lt('time_sent', endDate)
        .order('time_sent', { ascending: false })
        .limit(200)

      if (numericClientId) {
        query = query.eq('client_id', numericClientId)
      }

      const { data, error } = await query
      if (error) throw error

      setDetailRecords(data || [])
    } catch (error) {
      console.error('Error fetching detail records:', error)
      setDetailRecords([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch chart data - get ALL records for accurate chart data
  const fetchChartData = async () => {
    try {
      setChartLoading(true)
      const { startDate, endDate } = getDateRange()
      const numericClientId = await resolveClientFilter(clientFilter)

      // Server-side aggregation via RPC — no row-level fetch needed
      const { data: rpcResult, error } = await (supabase as any).rpc('get_email_sent_chart_data', {
        start_date: startDate,
        end_date: endDate,
        target_client_id: numericClientId || null
      })

      if (error) throw error
      const chartData = rpcResult as any

      // Process email type data from RPC
      const emailTypes = chartData?.email_types || []
      const total = emailTypes.reduce((sum: number, t: any) => sum + (t.count || 0), 0)
      const emailTypeChartData: EmailTypeData[] = emailTypes.map((t: any) => ({
        type: t.type === 'real_person' ? 'Real Person' : t.type === 'company_generic' ? 'Company' : t.type,
        count: t.count || 0,
        percentage: total > 0 ? Math.round((t.count / total) * 100) : 0
      }))
      setEmailTypeData(emailTypeChartData)

      // Process daily trends from RPC
      const dailyTrends = chartData?.daily_trends || []
      const dailyChartData: DailyEmailData[] = dailyTrends.map((d: any) => {
        const dateObj = new Date(d.date)
        return {
          date: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          fullDate: dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          emails: d.emails || 0,
          realPerson: d.real_person || 0,
          company: d.company || 0,
          sequence1: d.sequence1 || 0,
          sequence2: d.sequence2 || 0,
          sequence3: d.sequence3 || 0,
          sequenceOther: d.sequence_other || 0
        }
      })
      setDailyData(dailyChartData)

      // Process campaign data from RPC
      const topCampaigns = chartData?.top_campaigns || []
      const campaignCounts: Record<string, number> = {}
      topCampaigns.forEach((c: any) => { campaignCounts[c.campaign] = c.count })

      const campaignTotal = Object.values(campaignCounts).reduce((sum: number, count: number) => sum + count, 0)
      const campaignChartData: CampaignData[] = Object.entries(campaignCounts)
        .map(([campaign, count]) => ({
          campaign,
          count: count as number,
          percentage: campaignTotal > 0 ? Math.round(((count as number) / campaignTotal) * 100) : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10) // Top 10 campaigns

      setCampaignData(campaignChartData)

      // Process daily campaign trends from RPC
      const dailyCampaignTrends = chartData?.daily_campaign_trends || []
      const dailyCampaignMap = new Map<string, Record<string, number>>()

      dailyCampaignTrends.forEach((row: any) => {
        const date = row.date
        const current = dailyCampaignMap.get(date) || {}
        current[row.campaign] = row.count
        dailyCampaignMap.set(date, current)
      })

      const dailyCampaignChartData: DailyCampaignData[] = Array.from(dailyCampaignMap.entries())
        .map(([date, counts]) => {
          const dateObj = new Date(date)
          return {
            date: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            fullDate: dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
            ...counts
          }
        })
        .sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime())

      setDailyCampaignData(dailyCampaignChartData)
    } catch (error) {
      console.error('Error fetching chart data:', error)
      setEmailTypeData([])
      setDailyData([])
    } finally {
      setChartLoading(false)
    }
  }

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    fetchMetrics()
    fetchDetailRecords()
    fetchChartData()
  }, [timeFrame, clientFilter, customDateRange])

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k'
    return num.toString()
  }

  const handleRecordClick = (record: any) => {
    setSelectedRecord(record)
    setModalOpen(true)
  }

  const handleExport = async (tableFilters?: { searchTerm?: string; columnFilters?: Record<string, string> }) => {
    try {
      console.log('🚀 Starting export with filters:', tableFilters)

      // Fetch ALL records for export using pagination, then apply table filters
      const { startDate, endDate } = getDateRange()
      const numericClientId = await resolveClientFilter(clientFilter)

      let allData: any[] = []
      let from = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        let query = supabase
          .from('obsync_dynamic_reports')
          .select(`
            id,
            event_type,
            email_type,
            from_email,
            to_email,
            to_name,
            campaign_name,
            campaign_id,
            client_id,
            subject,
            time_sent,
            created_at,
            category,
            sent_message_body,
            message,
            custom_email_message,
            custom_subject,
            preview_text,
            sent_message,
            lead_email,
            sequence_number,
            campaign_status,
            message_id,
            stats_id,
            updated_at
          `)
          .eq('event_type', 'EMAIL_SENT')
          .not('time_sent', 'is', null)
          .gte('time_sent', startDate)
          .lt('time_sent', endDate)
          .order('time_sent', { ascending: false })
          .range(from, from + pageSize - 1)

        // Apply client filter
        if (numericClientId) {
          query = query.eq('client_id', numericClientId)
        }

        const { data, error } = await query
        
        if (error) throw error
        
        if (data && data.length > 0) {
          allData = [...allData, ...data]
          from += pageSize
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      // Apply table filters to the complete dataset
      let filteredData = allData
      
      if (tableFilters) {
        filteredData = allData.filter(item => {
          // Search filter
          const matchesSearch = !tableFilters.searchTerm || 
            Object.values(item).some(value => 
              value?.toString().toLowerCase().includes(tableFilters.searchTerm!.toLowerCase())
            )
          
          // Column-specific filters
          const matchesColumnFilters = !tableFilters.columnFilters || Object.entries(tableFilters.columnFilters).every(([key, value]) => {
            if (!value) return true
            const itemValue = item[key]?.toString().toLowerCase() || ''
            return itemValue.includes(value.toLowerCase())
          })
          
          return matchesSearch && matchesColumnFilters
        })
      }

      console.log(`📊 Export data processed:`, {
        totalFetched: allData.length,
        afterFiltering: filteredData.length,
        appliedFilters: tableFilters
      })
      
      const csvContent = [
        ['Time Sent (Filter Field)', 'Created At', 'Email Type', 'From', 'To', 'Recipient', 'Campaign', 'Subject', 'Sequence'].join(','),
        ...filteredData.map(record => [
          record.time_sent ? new Date(record.time_sent).toLocaleString() : 'N/A',
          record.created_at ? new Date(record.created_at).toLocaleString() : 'N/A',
          record.email_type || 'Unknown',
          record.from_email || 'N/A',
          record.to_email || 'N/A',
          record.to_name || 'Unknown',
          record.campaign_name || 'Unknown Campaign',
          record.subject || 'N/A',
          record.sequence_number || ''
        ].map(value => `"${value}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `email-send-records-${timeFrame}-${new Date().toISOString().split('T')[0]}-${filteredData.length}-records.csv`
      a.click()
      URL.revokeObjectURL(url)
      
      console.log(`✅ Exported ${filteredData.length} email send records (filtered from ${allData.length} total)`)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const columns = [
    { 
      header: "Time Sent (Filter Field)", 
      key: "time_sent", 
      render: (value: string) => 
        <div className="font-mono text-sm">
          <div className="font-semibold text-primary">📅 {value ? new Date(value).toLocaleString() : 'N/A'}</div>
          <div className="text-xs text-muted-foreground">Filter field</div>
        </div>
    },
    { 
      header: "Created At", 
      key: "created_at", 
      render: (value: string) => 
        <div className="font-mono text-sm">
          <div>{value ? new Date(value).toLocaleString() : 'N/A'}</div>
          <div className="text-xs text-muted-foreground">DB record time</div>
        </div>
    },
    { 
      header: "Email Type", 
      key: "email_type", 
      render: (value: string) => 
        value === 'real_person' ? (
          <Badge variant="default" className="text-xs bg-blue-100 text-blue-800">Real Person</Badge>
        ) : value === 'company_generic' ? (
          <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">Company</Badge>
        ) : (
          <Badge variant="outline" className="text-xs">{value || 'Unknown'}</Badge>
        )
    },
    { 
      header: "From", 
      key: "from_email", 
      render: (value: string) => 
        <div className="font-mono text-sm">{value || 'N/A'}</div>
    },
    { 
      header: "To", 
      key: "to_email", 
      render: (value: string) => 
        <div className="font-mono text-sm">{value || 'N/A'}</div>
    },
    { 
      header: "Recipient", 
      key: "to_name", 
      render: (value: string) => 
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          {value || 'Unknown'}
        </div>
    },
    { 
      header: "Campaign", 
      key: "campaign_name", 
      render: (value: string, record: any) => 
        <div className="space-y-1">
          <div className="font-medium">{value || 'Unknown Campaign'}</div>
          {record.campaign_id && (
            <div className="text-xs text-muted-foreground font-mono">
              ID: {record.campaign_id}
            </div>
          )}
        </div>
    },
    { 
      header: "Subject", 
      key: "subject", 
      render: (value: string) => 
        <div className="max-w-[200px] truncate" title={value || 'N/A'}>
          {value || 'N/A'}
        </div>
    },
    { 
      header: "Sequence", 
      key: "sequence_number", 
      render: (value: number) => 
        value ? (
          <Badge variant="outline" className="text-xs">
            #{value}
          </Badge>
        ) : null
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Timeframe Selector */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        {onTimeFrameChange && onCustomDateRangeChange && (
          <TimeframeSelector
            timeFrame={timeFrame}
            customDateRange={customDateRange}
            onTimeFrameChange={onTimeFrameChange}
            onCustomDateRangeChange={onCustomDateRangeChange}
          />
        )}
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Emails Sent Dashboard</h1>
        <p className="text-muted-foreground">Track email sends with daily insights and comprehensive filtering</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Emails Sent</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.totalEvents)}</div>
            <p className="text-xs text-muted-foreground">Emails sent in selected timeframe</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Daily Average</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.dailyAverage)}</div>
            <p className="text-xs text-muted-foreground">Emails sent per day in timeframe</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Campaigns</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.activeCampaigns)}</div>
            <p className="text-xs text-muted-foreground">Campaigns with email sends</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Type Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Email Type Distribution
            </CardTitle>
            <CardDescription>Breakdown of emails sent to real people vs companies</CardDescription>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Loading chart data...
              </div>
            ) : emailTypeData.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={emailTypeData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ type, percent }: any) => `${type}: ${percent}%`}
                    >
                      {emailTypeData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={
                            entry.type === 'Real Person' ? '#3b82f6' : 
                            entry.type === 'Company' ? '#f59e0b' : '#6b7280'
                          } 
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-1 gap-2">
                  {emailTypeData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ 
                            backgroundColor: item.type === 'Real Person' ? '#3b82f6' : 
                                           item.type === 'Company' ? '#f59e0b' : '#6b7280'
                          }} 
                        />
                        <span className="text-sm font-medium">{item.type}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{formatNumber(item.count)}</div>
                        <div className="text-xs text-muted-foreground">{item.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available for selected timeframe
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Email Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Daily Email Trends by Sequence
            </CardTitle>
            <CardDescription>Emails sent per day with breakdown by sequence numbers (1, 2, 3+)</CardDescription>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Loading chart data...
              </div>
            ) : dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fullDate" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      formatNumber(value as number), 
                      name === 'sequence1' ? 'Sequence 1' : 
                      name === 'sequence2' ? 'Sequence 2' : 
                      name === 'sequence3' ? 'Sequence 3' : 
                      name === 'sequenceOther' ? 'Sequence 4+' :
                      name === 'realPerson' ? 'Real Person' : 
                      name === 'company' ? 'Company' : name
                    ]}
                  />
                  <Bar dataKey="sequence1" stackId="a" fill="#22c55e" name="Sequence 1" />
                  <Bar dataKey="sequence2" stackId="a" fill="#3b82f6" name="Sequence 2" />
                  <Bar dataKey="sequence3" stackId="a" fill="#f59e0b" name="Sequence 3" />
                  <Bar dataKey="sequenceOther" stackId="a" fill="#ef4444" name="Sequence 4+" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available for selected timeframe
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Campaign Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Campaigns Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top Campaigns by Volume
            </CardTitle>
            <CardDescription>Top 10 campaigns by email volume in selected timeframe</CardDescription>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Loading chart data...
              </div>
            ) : campaignData.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={campaignData} layout="horizontal" margin={{ top: 20, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="campaign" 
                      width={80}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                    />
                    <Tooltip 
                      formatter={(value, name) => [formatNumber(value as number), 'Emails Sent']}
                      labelFormatter={(label) => `Campaign: ${label}`}
                    />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No campaign data available for selected timeframe
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Campaign Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Daily Campaign Trends
            </CardTitle>
            <CardDescription>Daily email trends for top 5 campaigns</CardDescription>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Loading chart data...
              </div>
            ) : dailyCampaignData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyCampaignData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fullDate" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [formatNumber(value as number), name as string]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  {campaignData.slice(0, 5).map((campaign, index) => (
                    <Bar 
                      key={campaign.campaign}
                      dataKey={campaign.campaign} 
                      stackId="campaigns"
                      fill={`hsl(${(index * 70) % 360}, 70%, 50%)`}
                      name={campaign.campaign.length > 20 ? campaign.campaign.substring(0, 20) + '...' : campaign.campaign}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No daily campaign data available for selected timeframe
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Data Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Email Send Records</CardTitle>
              <CardDescription>Comprehensive view of all sent emails with advanced filtering and export</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <EnhancedDataTable
            columns={columns}
            data={detailRecords}
            onRecordClick={handleRecordClick}
            onExport={handleExport}
            loading={loading}
            searchPlaceholder="Search email records..."
            totalRecords={detailRecords.length}
          />
        </CardContent>
      </Card>
      
      <EmailEventDetailModal
        event={selectedRecord}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  )
}